/** Custom React Flow node renderer. The default react-flow node is a
 *  plain box; we paint each node with its type's brand colour, an
 *  accent dot, and the label, so the canvas reads as Memso, not as a
 *  generic graph viewer. */
import { Handle, Position, type NodeProps } from "reactflow";
import { nodeStyleFor } from "./palette";

export interface WeaveNodeData {
  label: string;
  type: string;
  selected: boolean;
  /** Accent-dot diameter in px (8..32), log-scaled off the node's
   *  total_strength so an active sibling reads bigger than a dormant
   *  vendor without breaking the label card layout. */
  size?: number;
  totalStrength?: number;
}

export function WeaveNode({ data, selected }: NodeProps<WeaveNodeData>) {
  const style = nodeStyleFor(data.type);
  const ring = selected
    ? "ring-2 ring-offset-2 ring-offset-transparent"
    : "ring-1";
  const dot = Math.min(32, Math.max(8, data.size ?? 10));
  return (
    <div
      className={`group relative flex items-center gap-2 rounded-xl px-3 py-2 shadow-md transition-shadow hover:shadow-lg ${ring}`}
      style={{
        background: style.fill,
        borderColor: style.border,
        color: style.text,
        border: `1px solid ${style.border}`,
        minWidth: 140,
        maxWidth: 200,
        ["--tw-ring-color" as any]: style.accent,
      }}
      title={
        data.totalStrength != null
          ? `${data.type}: ${data.label} · strength ${data.totalStrength.toFixed(2)}`
          : `${data.type}: ${data.label}`
      }
    >
      <span
        aria-hidden
        className="inline-block rounded-full flex-shrink-0"
        style={{ background: style.accent, width: dot, height: dot }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">
          {data.type}
        </div>
        <div className="text-sm font-semibold truncate">{data.label}</div>
      </div>
      <Handle type="target" position={Position.Top} className="!opacity-0" />
      <Handle type="source" position={Position.Bottom} className="!opacity-0" />
    </div>
  );
}

export const nodeTypes = { weave: WeaveNode };
